import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useMotionTemplate } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './uiHomePage/button.tsx';
import { getAuth, onAuthStateChanged, signOut, User } from 'firebase/auth';

interface MobileNavProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  textColor: string;
}

const MobileNav: React.FC<MobileNavProps> = ({ isOpen, setIsOpen, textColor }) => {
  const { scrollY } = useScroll();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Animate logo and menu button only
  const logoScale = useTransform(scrollY, [0, 100], [1, 0.92]);
  const logoX = useTransform(scrollY, [0, 100], [0, 10]);
  const menuScale = useTransform(scrollY, [0, 100], [1, 0.92]);
  const menuX = useTransform(scrollY, [0, 100], [0, -10]);
  
  // Enhanced glassmorphic background animations
  const gradientOpacity = useTransform(scrollY, [0, 10], [0, 1]);
  const bgOpacity = useTransform(scrollY, [0, 10, 100], [1, 1, 1]); // always visible base
  const pillOpacity = useTransform(scrollY, [0, 40], [1, 1]);
  const bgBlur = useTransform(scrollY, [0, 100], [0, 16]);
  const bgFilter = useMotionTemplate`blur(${bgBlur}px)`;
  const pillScale = useTransform(scrollY, [0, 100], [1, 1.02]);
  const pillY = useTransform(scrollY, [0, 100], [0, 4]);

  // Scroll or navigate to section
  const handleSectionNav = (sectionId: string) => {
    setIsOpen(false);
    if (window.location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleAuthAction = () => {
    if (user) {
      const auth = getAuth();
      signOut(auth)
        .then(() => {
          navigate('/');
          setIsOpen(false);
        })
        .catch(() => {
          setError('Failed to log out');
        });
    } else {
      navigate('/login');
      setIsOpen(false);
    }
  };

  // Show glassmorphic card menu overlay
  return (
    <>
      {error && <div className="fixed top-0 right-0 bg-red-500 text-white p-2 m-2 rounded z-50">{error}</div>}
      <div className="fixed top-0 left-0 right-0 z-40 flex md:hidden justify-center w-full py-4 pointer-events-none">
        {/* Glassmorphic pill (fades in on scroll) */}
        <motion.div 
          className="relative flex items-center justify-between w-full max-w-[400px] mx-auto px-4 py-2 pointer-events-auto "
          style={{ 
            scale: pillScale,
            y: pillY,
            opacity: pillOpacity
          }}
        >
          {/* Glassmorphic background: base layer (transparent/white) */}
          <motion.div 
            className="absolute inset-0 rounded-full -z-10 border border-white/40 shadow-xl bg-white/10"
            style={{
              opacity: bgOpacity,
              filter: bgFilter,
              boxShadow: '0 8px 40px 0 rgba(255,105,180,0.10)',
              backdropFilter: 'blur(18px)'
            }}
          />
          {/* Glassmorphic background: gradient layer (fades in on scroll) */}
          <motion.div
            className="absolute inset-0 rounded-full -z-10 border border-black/20 shadow-xl"
            style={{
              opacity: gradientOpacity,
              background: 'linear-gradient(90deg, #FF69B4 0%, #FFDAB9 100%)',
              filter: bgFilter,
              boxShadow: '0 8px 40px 0 rgba(255,105,180,0.10)',
              backdropFilter: 'blur(18px)'
            }}
          />
          <Link to="/" className="flex items-center pl-2">
            <img src="/logos/full_band_1.svg" alt="Wedify Logo" className="w-8 h-8 drop-shadow-lg hover:scale-110 transition-transform duration-300 filter brightness-110" />
            <span className="ml-2 text-2xl font-semibold" style={{color: 'rgba(0, 0, 0, 0.9)'}}>Wedify</span>
          </Link>
          <motion.div
            className="pr-2"
            style={{ scale: menuScale, x: menuX }}
          >
            <Button
              variant="ghost"
              className="transition-colors duration-300 text-black hover:text-[#FF4E8E]"
              onClick={() => setIsOpen(!isOpen)}
            >
              <Menu size={32} />
            </Button>
          </motion.div>
        </motion.div>
      </div>
      {/* Glassmorphic Card Mobile Menu Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Blurry dark background */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[6px]" onClick={() => setIsOpen(false)} />
          {/* Card */}
          <div className="relative z-10 w-full max-w-xs mx-auto rounded-2xl border border-white/30 bg-white/30 shadow-2xl backdrop-blur-xl p-6 flex flex-col items-stretch">
            <button className="absolute top-4 right-4 p-2 text-gray-700 hover:text-pink-500" onClick={() => setIsOpen(false)}>
              <X size={28} />
            </button>
            <nav className="flex flex-col space-y-6 mt-2 mb-8 text-left">
              {user ? (
                <>
                  <Link to="/plan" onClick={() => setIsOpen(false)} className="text-gray-900 text-2xl font-medium text-left">Plan</Link>
                  <Link to="/design" onClick={() => setIsOpen(false)} className="text-gray-900 text-2xl font-medium text-left">Design</Link>
                  <Link to="/explore" onClick={() => setIsOpen(false)} className="text-gray-900 text-2xl font-medium text-left">Explore</Link>
                  <Link to="/vendors" onClick={() => setIsOpen(false)} className="text-gray-900 text-2xl font-medium text-left">Vendors</Link>
                  <Link to="/user-profile" onClick={() => setIsOpen(false)} className="text-gray-900 text-2xl font-medium text-left">Profile</Link>
                </>
              ) : (
                <>
                  <button onClick={() => handleSectionNav('features')} className="text-gray-900 text-2xl font-medium text-left">Features</button>
                  <button onClick={() => handleSectionNav('how-it-works')} className="text-gray-900 text-2xl font-medium text-left">How It Works</button>
                  <button onClick={() => handleSectionNav('planning')} className="text-gray-900 text-2xl font-medium text-left">Planning</button>
                  <Link to="/vendors" onClick={() => setIsOpen(false)} className="text-gray-900 text-2xl font-medium text-left">Vendors</Link>
                  </>
              )}
            </nav>
            <div className="flex flex-col space-y-4 w-full mt-auto">
              {!user ? (
                <>
                  <Link
                    to="/login"
                    onClick={() => setIsOpen(false)}
                    className="w-[270px] h-[36px] flex items-center justify-center rounded-full bg-gray-300/60 text-lg text-gray-800 font-semibold text-center mx-auto"
                  >
                    Log In
                  </Link>
                  <Button onClick={() => { setIsOpen(false); navigate('/login'); }} className="w-full rounded-full bg-[#FF4E8E] text-lg text-white font-semibold py-3 text-center">Sign Up</Button>
                </>
              ) : (
                <Button onClick={handleAuthAction} className="w-full rounded-full bg-gray-900 text-lg text-white font-semibold py-3 text-center">Log Out</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileNav;